<template>
  <v-dialog
    v-model="dialogProxy"
    :max-width="showPreview ? 'calc(100% - 550px)' : 560"
    persistent
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Invoice Settings</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          @click="showPreview = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="invoice-dialog-layout">
          <div class="invoice-form-pane">
            <v-form ref="invoiceForm" lazy-validation>
              <div class="pa-6">
                <v-alert
                  v-if="hasMixedItemVat"
                  dense
                  outlined
                  type="info"
                  class="mb-4"
                >
                  Mixed item VAT: {{ itemVatSummary }}. The invoice uses the VAT rate stored on each Sales Order item.
                </v-alert>

                <v-combobox
                  v-else
                  v-model="localForm.tax"
                  :items="taxItems"
                  label="Fallback tax rate *"
                  outlined
                  dense
                  clearable
                  :rules="taxRules"
                  @change="normalizeTax"
                />

                <v-autocomplete
                  v-model="localForm.proformaId"
                  :items="availableProformas"
                  label="Related proforma"
                  item-text="label"
                  item-value="text"
                  outlined
                  dense
                  clearable
                  hint="Optional. If empty, NULL will be sent."
                  persistent-hint
                  no-data-text="No proformas found for this order"
                >
                  <template v-slot:item="{ item }">
                    <div class="d-flex align-center justify-space-between w-100">
                      <div>
                        <div class="font-weight-medium">{{ item.documentNumber }}</div>
                        <div class="caption grey--text text--darken-1">
                          {{ item.issuedLabel }}
                        </div>
                      </div>

                      <v-chip
                        x-small
                        label
                        :color="item.isPaid ? 'success' : 'grey lighten-1'"
                        :text-color="item.isPaid ? 'white' : 'black'"
                      >
                        {{ item.isPaid ? 'Paid' : 'Pending' }}
                      </v-chip>
                    </div>
                  </template>

                  <template v-slot:selection="{ item }">
                    <div class="d-flex align-center">
                      <span>{{ item.documentNumber }}</span>
                      <v-chip
                        v-if="item.isPaid"
                        x-small
                        label
                        color="success"
                        text-color="white"
                        class="ml-2"
                      >
                        Paid
                      </v-chip>
                    </div>
                  </template>
                </v-autocomplete>

                <div
                  class="paid-switch-card mb-5"
                  :class="{ 'paid-switch-card--active': localForm.isPaid }"
                >
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center">
                      <v-avatar size="36" class="paid-switch-icon mr-3">
                        <v-icon small>
                          {{ localForm.isPaid ? 'mdi-check' : 'mdi-clock-outline' }}
                        </v-icon>
                      </v-avatar>

                      <div>
                        <div class="subtitle-2 font-weight-medium">
                          Is paid
                        </div>
                        <div class="caption grey--text text--darken-1">
                          {{ localForm.isPaid ? 'Invoice will be marked as paid.' : 'Invoice will be created as unpaid.' }}
                        </div>
                      </div>
                    </div>

                    <v-switch
                      v-model="localForm.isPaid"
                      class="paid-switch ma-0"
                      inset
                      dense
                      hide-details
                      color="success"
                    >
                      <template v-slot:label>
                        <span
                          class="font-weight-medium"
                          :class="localForm.isPaid ? 'success--text' : 'grey--text text--darken-1'"
                        >
                          {{ localForm.isPaid ? 'Yes' : 'No' }}
                        </span>
                      </template>
                    </v-switch>
                  </div>
                </div>

                <v-row dense>
                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="dateMenus.fulfillmentDate"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="localForm.fulfillmentDate"
                          label="Date of completion"
                          outlined
                          dense
                          hint="Optional"
                          persistent-hint
                          readonly
                          clearable
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="localForm.fulfillmentDate = null"
                        />
                      </template>

                      <v-date-picker
                        v-model="localForm.fulfillmentDate"
                        no-title
                        @input="dateMenus.fulfillmentDate = false"
                      />
                    </v-menu>

                    <div class="d-flex flex-column mt-2  mb-2">
                      <div>Add days:</div>
                      <div class="d-flex flex-wrap">
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 0)">Today</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 1)">+1</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 2)">+2</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 3)">+3</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('fulfillmentDate', 5)">+5</v-btn>
                        <v-btn text @click="setTodayPlusDays('fulfillmentDate', 8)">+8</v-btn>
                      </div>
                    </div>
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-menu
                      v-model="dateMenus.dueDate"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="localForm.dueDate"
                          label="Payment due date"
                          outlined
                          dense
                          hint="Optional"
                          persistent-hint
                          readonly
                          clearable
                          v-bind="attrs"
                          v-on="on"
                          @click:clear="localForm.dueDate = null"
                        />
                      </template>

                      <v-date-picker
                        v-model="localForm.dueDate"
                        no-title
                        @input="dateMenus.dueDate = false"
                      />
                    </v-menu>

                    <div class="d-flex flex-column mt-2 mb-2">
                      <div>Add days:</div>
                      <div class="d-flex flex-wrap">
                        <v-btn text class="mr-2" @click="setTodayPlusDays('dueDate', 0)">Today</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('dueDate', 1)">+1</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('dueDate', 2)">+2</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('dueDate', 3)">+3</v-btn>
                        <v-btn text class="mr-2" @click="setTodayPlusDays('dueDate', 5)">+5</v-btn>
                        <v-btn text @click="setTodayPlusDays('dueDate', 8)">+8</v-btn>
                      </div>
                    </div>
                  </v-col>
                </v-row>

                <v-textarea
                  v-model="localForm.note"
                  label="Comment"
                  outlined
                  dense
                  auto-grow
                  rows="3"
                  hint="Optional"
                  persistent-hint
                />
              </div>
            </v-form>
          </div>

          <v-expand-x-transition>
            <div v-if="showPreview" class="invoice-preview-pane">
              <v-divider vertical />

              <div class="pa-6 preview-pane-inner">
                <InvoicePreviewPanel
                  document-type="invoice"
                  :order="invoiceDatas"
                  :settings="previewSettings"
                  :seller="seller"
                  :currency="computeSuffix"
                />
              </div>
            </div>
          </v-expand-x-transition>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="px-6 py-4">
        <v-spacer />

        <v-btn
          v-if="invoiceDatas"
          color="primary"
          class="mr-2"
          :disabled="loading"
          @click="openPreview"
        >
          {{ !showPreview ? 'Preview invoice' : 'Close preview' }}
        </v-btn>

        <v-btn
          text
          :disabled="loading"
          @click="closeDialog"
        >
          Cancel
        </v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!isValid"
          @click="submit"
        >
          Issue
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import InvoicePreviewPanel from './InvoicePreviewPanel.vue'
import Swal from "sweetalert2";


export default {
  name: 'InvoiceDialog',

  components: {
    InvoicePreviewPanel,
  },

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: [Number, String],
      required: true,
    },
    initialData: {
      type: Object,
      default: () => ({
        tax: null,
        fulfillmentDate: null,
        dueDate: null,
        note: null,
        proformaId: null,
        isPaid: false,
      }),
    },
    invoiceDatas: {
      type: Object,
      default: () => null,
    },
    seller: {
      type: Object,
      default: () => ({
        name: 'Kismetál Kft.',
        address: null,
        taxNumber: null,
        email: null,
        phone: null,
        bankAccount: null,
      }),
    },
    taxItems: {
      type: Array,
      default: () => [
        'TAHK', 'TAM', 'AAM', 'EUT', 'EUKT', 'F.AFA', 'K.AFA', 'HO', 'EUE',
        'EUFADE', 'EUFAD37', 'ATK', 'NAM', 'EAM', 'KBAUK', 'KBAET',
        '0', '1', '2', '2.1', '3', '4', '4.8', '5', '5.5', '6', '7', '7.7',
        '8', '8.1', '9', '9.5', '10', '11', '12', '13', '13.5', '14', '15',
        '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '25.5',
        '26', '27',
      ],
    },
  },

  data() {
    return {
      showPreview: false,
      taxDefaultApplied: false,
      dateMenus: {
        fulfillmentDate: false,
        paymentDeadline: false,
        dueDate: false,
      },
      datas: null,
      localForm: {
        tax: null,
        fulfillmentDate: null,
        dueDate: null,
        note: null,
        proformaId: null,
        isPaid: false,
      },
    }
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },

    itemVatCodes() {
      return this.collectItemVatCodes()
    },

    hasMixedItemVat() {
      return this.itemVatCodes.length > 1
    },

    itemVatSummary() {
      return this.itemVatCodes.join(', ')
    },

    isValid() {
      if (!this.hasMixedItemVat && !this.localForm.tax) return false
      if (!this.isValidTax(this.localForm.tax)) return false

      return true
    },

    computeSuffix() {
      return this.datas?.priceList?.currency_id ?? 'HUF'
    },

    availableProformas() {
      if (!Array.isArray(this.invoiceDatas?.documents)) {
        return []
      }

      return this.invoiceDatas.documents
        .filter((document) => this.normalizeDocumentType(document.type) === 'proforma')
        .sort((a, b) => {
          const aDate = new Date(a.issued_at || a.created_at || 0).getTime()
          const bDate = new Date(b.issued_at || b.created_at || 0).getTime()

          return bDate - aDate
        })
        .map((document) => {
          const paidAmount = Number(document.paid_gross_amount || 0)
          const isPaid = !!document.paid_at || String(document.status || '').toLowerCase() === 'paid' || paidAmount > 0
          const documentNumber = document.document_number || `#${document.id}`
          const issuedAt = this.formatDocumentDate(document.issued_at || document.created_at)

          return {
            id: document.id,
            documentNumber,
            issuedLabel: issuedAt !== '-' ? `Issued: ${issuedAt}` : 'Issue date unknown',
            isPaid,
            label: `${documentNumber}${isPaid ? ' • Paid' : ' • Pending'}`,
            text: `${documentNumber},${document.id}`,
          }
        })
    },

    previewSettings() {
      return {
        id: this.orderId,
        vat: this.hasMixedItemVat ? null : (this.localForm.tax || null),
        tax: this.hasMixedItemVat ? null : (this.localForm.tax || null),
        fulfillmentDate: this.localForm.fulfillmentDate || null,
        paymentDeadline: this.localForm.dueDate || null,
        note: this.localForm.note || null,
        proforma_id: this.localForm.proformaId || null,
        is_paid: !!this.localForm.isPaid,
      }
    },

    taxRules() {
      return [
        v => !!v || 'You must select one tax rate',
        v => this.taxItems.includes(String(v).trim().toUpperCase()) || 'You can select only the tax rate listed',
      ]
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) {
          this.syncForm()
        } else {
          this.showPreview = false
        }
      },
    },

    initialData: {
      deep: true,
      handler() {
        if (this.value) {
          this.syncForm()
        }
      },
    },

    invoiceDatas: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val) {
          this.datas = val
          this.calculateWeight()
          this.calculateFAFA()
          this.setInitialTax()
        }
      },
    },

    'localForm.tax'(newTax) {
      if (this.hasMixedItemVat) {
        return
      }

      const normalized = String(newTax || '').trim().toUpperCase()
      const text = 'Az ÁFA törvény 142§ (1) bekezdés j. pontja alapján az adó fizetésére a vevő kötelezett.'

      this.calculateWeight()

      if (normalized === 'F.AFA' && (!this.localForm.note || !this.localForm.note.includes(text))) {
        this.localForm.note += (this.localForm.note ? '\n' : '') + text
      }
    },
  },

  methods: {
    formatDateForInput(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },

    setTodayPlusDays(field, days) {
      const date = new Date()
      date.setHours(0, 0, 0, 0)
      date.setDate(date.getDate() + days)
      this.localForm[field] = this.formatDateForInput(date)
    },

    normalizeItemVat(value) {
      if (value === null || value === undefined || value === '') return null

      const normalized = String(value).trim().toUpperCase().replace(',', '.')
      return this.taxItems.includes(normalized) ? normalized : null
    },

    normalizeBoolean(value) {
      return value === true || value === 1 || value === '1' || value === 'true'
    },

    normalizeSalesPointIdentifier(value) {
      if (value === null || value === undefined || typeof value === 'object') return ''

      return String(value)
        .trim()
        .toLowerCase()
        .replace(/^https?:\/\//, '')
        .replace(/^www\./, '')
        .replace(/[^a-z0-9]/g, '')
    },

    isKismetalVaskerSalesPoint() {
      const salesPoint = this.invoiceDatas?.salesPoint || this.invoiceDatas?.sales_point || {}
      const candidates = [
        salesPoint,
        salesPoint?.name,
        salesPoint?.slug,
        salesPoint?.domain,
        salesPoint?.website,
        salesPoint?.website?.domain,
        salesPoint?.site?.domain,
        this.invoiceDatas?.sales_point,
        this.invoiceDatas?.sales_point_name,
        this.invoiceDatas?.salesPointName,
      ]

      return candidates.some(value => this.normalizeSalesPointIdentifier(value).includes('kismetalvasker'))
    },

    isEurOrder() {
      const orderCurrency = String(
        this.invoiceDatas?.priceList?.currency_id
        || this.invoiceDatas?.price_list?.currency_id
        || this.invoiceDatas?.currency_id
        || ''
      ).trim().toUpperCase()

      if (orderCurrency === 'EUR') return true

      const items = Array.isArray(this.invoiceDatas?.items) ? this.invoiceDatas.items : []
      const itemCurrencies = items
        .map(item => String(
          item?.currency_id
          || item?.currency?.id
          || item?.priceList?.currency_id
          || item?.price_list?.currency_id
          || ''
        ).trim().toUpperCase())
        .filter(Boolean)

      return itemCurrencies.length > 0 && itemCurrencies.every(currency => currency === 'EUR')
    },

    resolveAutomaticTaxDefault() {
      // EUR mindig erősebb szabály: a dialógus EUT-tal indul.
      if (this.isEurOrder()) return 'EUT'

      if (
        this.isKismetalVaskerSalesPoint()
        && this.normalizeBoolean(this.invoiceDatas?.zero_tax)
      ) {
        return 'F.AFA'
      }

      return null
    },

    resolveStoredItemVat(item) {
      const candidates = [
        item?.vat_name,
        item?.vat_value,
        item?.tax,
        item?.item?.vat_name,
        item?.item?.vat_value,
        item?.item?.tax,
      ]

      for (const candidate of candidates) {
        const normalized = this.normalizeItemVat(candidate)

        if (normalized) {
          return normalized
        }
      }

      return null
    },

    resolveItemVat(item) {
      // A Sales Order tételén tárolt ÁFA-kulcs az elsődleges.
      // Az EUR / sales point szabály csak hiányzó tételkulcs esetén fallback.
      const storedItemVat = this.resolveStoredItemVat(item)

      if (storedItemVat) return storedItemVat

      const automaticTax = this.resolveAutomaticTaxDefault()
      const zeroTax = this.normalizeBoolean(this.invoiceDatas?.zero_tax)
      const reverseTax = this.normalizeBoolean(this.invoiceDatas?.reverse_tax)
      const isService = this.normalizeBoolean(item?.service)
        || this.normalizeBoolean(item?.item?.service)

      if (automaticTax) return automaticTax
      if (zeroTax) return '0'
      if (reverseTax && !isService) return 'F.AFA'

      return this.normalizeItemVat(this.invoiceDatas?.priceList?.vat_value)
        || this.normalizeItemVat(this.invoiceDatas?.price_list?.vat_value)
        || '27'
    },

    collectItemVatCodes() {
      const items = Array.isArray(this.invoiceDatas?.items) ? this.invoiceDatas.items : []
      return [...new Set(items.map(item => this.resolveItemVat(item)).filter(Boolean))]
    },

    normalizeTax() {
      if (this.localForm.tax === null || this.localForm.tax === '') {
        this.localForm.tax = null
        return
      }

      this.localForm.tax = String(this.localForm.tax).trim().toUpperCase()
    },

    isValidTax(tax) {
      if (tax === null || tax === '') return true

      const normalized = String(tax).trim().toUpperCase()
      return this.taxItems.includes(normalized)
    },

    normalizeDocumentType(type) {
      if (!type) return ''

      const normalized = String(type).toLowerCase().replace(/-/g, '_')

      if (normalized === 'preinvoice') return 'proforma'
      if (normalized === 'pre_invoice') return 'proforma'

      return normalized
    },

    syncForm() {
      // Egy új dialógus-megnyitás / form reset során egyszer engedjük az automatikus ÁFA-beállítást.
      this.taxDefaultApplied = false

      this.localForm = {
        tax: this.initialData?.tax ?? null,
        fulfillmentDate: this.initialData?.fulfillmentDate || null,
        dueDate: this.initialData?.dueDate || null,
        note: this.initialData?.note || null,
        proformaId: this.initialData?.proformaId ?? this.initialData?.proforma_id ?? null,
        isPaid: this.initialData?.isPaid ?? this.initialData?.is_paid ?? false,
      }

      this.normalizeTax()
      this.calculateWeight()
      this.calculateFAFA()
      this.setInitialTax()
    },

    openPreview() {
      if (this.showPreview) {
        this.showPreview = false
        return
      }

      this.normalizeTax()

      const valid = this.$refs.invoiceForm.validate()

      if (!valid) {
        return
      }

      this.showPreview = true
    },

    closeDialog() {
      this.showPreview = false
      this.syncForm()
      this.dialogProxy = false
    },

    submit() {
      this.normalizeTax()

      const valid = this.$refs.invoiceForm.validate()

      if (!valid) {
        return
      }

      const payload = {
        tax: this.hasMixedItemVat ? null : (this.localForm.tax || null),
        fulfillmentDate: this.localForm.fulfillmentDate || null,
        dueDate: this.localForm.dueDate || null,
        note: this.localForm.note ? this.localForm.note.trim() : null,
        proforma_id: this.localForm.proformaId || null,
        is_paid: !!this.localForm.isPaid,
      }

      Swal.fire({
          title: `<strong>ALERT</strong>`,
          icon: "warning",
          width: "600px",
          text: "Are you sure you want to create this invoice? This action cannot be undone.",
          showCloseButton: true,
          focusConfirm: false,
          confirmButtonText: "Yes, create it!",
          confirmButtonColor: "#00c300",
          infoColor: "#e33354",
          showCancelButton: true,
      }).then((result) => {
          if (result.isConfirmed) {
              this.$emit('submit', payload)
              //this.dialogProxy = false
          }
      });
    },

    calculateWeight() {
      if (!this.invoiceDatas || !Array.isArray(this.invoiceDatas.items) || this.invoiceDatas.items.length <= 0) {
        return
      }

      let sumWeight = 0

      this.invoiceDatas.items.forEach(element => {
        const unitWeight = Number(element.alt_unit_weight || 0) || Number(element.unit_weight || 0)
      
        if (unitWeight > 0) {
          sumWeight += unitWeight * Number(element.quantity || 0) * Number(element.num_of_pcs || 1)
        }
      })

      this.localForm.note = `Össztömeg: ${sumWeight.toFixed(1)} kg\n`
    },

    calculateFAFA() {
      if (!this.invoiceDatas) return

      const text = 'Az ÁFA törvény 142§ (1) bekezdés j. pontja alapján az adó fizetésére a vevő kötelezett.'
      const hasReverseChargeItem = this.itemVatCodes.includes('F.AFA')

      if (hasReverseChargeItem && (!this.localForm.note || !this.localForm.note.includes(text))) {
        this.localForm.note += (this.localForm.note ? '\n' : '') + text
      }
    },

    setInitialTax() {
      if (!this.invoiceDatas || this.taxDefaultApplied) return

      if (this.hasMixedItemVat) {
        this.localForm.tax = null
        this.taxDefaultApplied = true
        return
      }

      const automaticTax = this.resolveAutomaticTaxDefault()

      this.localForm.tax = this.itemVatCodes[0]
        || automaticTax
        || this.normalizeItemVat(this.localForm.tax)
        || this.normalizeItemVat(this.invoiceDatas?.priceList?.vat_value)
        || '27'

      // Innentől a felhasználó választása az irányadó; watcher nem írhatja felül.
      this.taxDefaultApplied = true
    },
    formatDocumentDate(value) {
      if (!value) return '-'

      const date = new Date(value)

      if (Number.isNaN(date.getTime())) return '-'

      return date.toLocaleDateString('hu-HU')
    },
  },
}
</script>

<style scoped>
.invoice-dialog-layout {
  display: flex;
  align-items: stretch;
  min-height: 620px;
  transition: all .5s ease-in-out;
}

.invoice-form-pane {
  flex: 0 0 560px;
  max-width: 560px;
}

.invoice-preview-pane {
  display: flex;
  flex: 1;
  min-width: 0;
  background: #fafafa;
}

.preview-pane-inner {
  flex: 1;
  min-width: 0;
  overflow: auto;
}

.paid-switch-card {
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fafafa;
  padding: 12px 14px;
  transition: border-color .2s ease, background-color .2s ease, box-shadow .2s ease;
}

.paid-switch-card--active {
  border-color: #4caf50;
  background: #f1f8f4;
  box-shadow: 0 2px 10px rgba(76, 175, 80, .12);
}

.paid-switch-icon {
  background: #eeeeee;
  color: #757575;
  transition: background-color .2s ease, color .2s ease;
}

.paid-switch-card--active .paid-switch-icon {
  background: #e8f5e9;
  color: #2e7d32;
}

.paid-switch {
  flex: 0 0 auto;
}
</style>